/**
 * GamificationContext - XP, levels, achievements, and streaks for Finding Your Voice
 *
 * Tracks experience points, level progression, achievement unlocks, and daily streaks.
 * Celebrates milestones with confetti animations.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { LEVELS, ACHIEVEMENTS, findNewlyUnlocked } from '../data/achievements';

const GamificationContext = createContext(null);

const STORAGE_KEY = 'fyvGamification';

// XP reward amounts
const XP_REWARDS = {
  moduleCompletion: 100,
  assessmentDone: 50,
  toolUsed: 25,
  sceneSetterRead: 30,
  dailyVisit: 10,
};

/**
 * Calculate the level for a given XP total
 */
const getLevelForXP = (xp) => {
  let currentLevel = LEVELS[0];
  for (const level of LEVELS) {
    if (xp >= level.xpRequired) {
      currentLevel = level;
    } else {
      break;
    }
  }
  return currentLevel;
};

/**
 * Try to fire confetti celebration
 */
const triggerCelebration = async () => {
  try {
    const confetti = (await import('canvas-confetti')).default;
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });
  } catch {
    // canvas-confetti not available, skip silently
  }
};

export const GamificationProvider = ({ children }) => {
  const [xp, setXP] = useState(0);
  const [level, setLevel] = useState(LEVELS[0]);
  const [achievements, setAchievements] = useState([]);
  const [streak, setStreak] = useState(0);
  const [daysVisited, setDaysVisited] = useState(0);
  const [lastActiveDate, setLastActiveDate] = useState(null);

  // Load from localStorage, then roll the daily streak forward from the
  // LOADED state. This must be one effect: a separate streak effect only sees
  // the initial (null) lastActiveDate and stomps the persisted streak back to 1.
  useEffect(() => {
    let parsed = null;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) parsed = JSON.parse(saved);
    } catch (err) {
      console.error('Failed to load gamification state:', err);
    }

    const savedXP = parsed?.xp || 0;
    setXP(savedXP);
    setLevel(getLevelForXP(savedXP));
    setAchievements(parsed?.achievements || []);

    const today = new Date().toDateString();
    const savedLastActive = parsed?.lastActiveDate || null;
    const savedStreak = parsed?.streak || 0;
    const savedDays = parsed?.daysVisited || 0;

    if (savedLastActive === today) {
      // Already visited today — keep both counters as saved
      setStreak(savedStreak);
      setDaysVisited(savedDays || 1);
    } else if (savedLastActive) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      // Consecutive day extends the streak; a gap resets it.
      // daysVisited is cumulative and only ever grows — badges hang off it.
      setStreak(savedLastActive === yesterday.toDateString() ? savedStreak + 1 : 1);
      setDaysVisited(savedDays + 1);
    } else {
      setStreak(1);
      setDaysVisited(1);
    }
    setLastActiveDate(today);
  }, []);

  // Save to localStorage
  const saveState = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        xp,
        achievements,
        streak,
        daysVisited,
        lastActiveDate,
      }));
    } catch (err) {
      console.error('Failed to save gamification state:', err);
    }
  }, [xp, achievements, streak, daysVisited, lastActiveDate]);

  // Auto-save when state changes
  useEffect(() => {
    const timeoutId = setTimeout(saveState, 500);
    return () => clearTimeout(timeoutId);
  }, [saveState]);

  /**
   * Add XP and check for level ups
   */
  const addXP = useCallback((amount, reason = '') => {
    setXP(prev => {
      const newXP = prev + amount;
      const newLevel = getLevelForXP(newXP);
      const oldLevel = getLevelForXP(prev);

      if (newLevel.number > oldLevel.number) {
        // Level up! Celebrate
        setLevel(newLevel);
        triggerCelebration();
      } else {
        setLevel(newLevel);
      }

      return newXP;
    });
  }, []);

  /**
   * Check and unlock achievements. Definitions and predicates live in
   * data/achievements.js; this merges the caller's progress snapshot with
   * gamification-side state, applies unlocks, and awards each achievement's
   * XP. Callers assemble the snapshot via ProgressContext.getAchievementContext.
   */
  const checkAchievements = useCallback((context = {}) => {
    const fullContext = {
      daysVisited,
      levelNumber: getLevelForXP(xp).number,
      ...context,
    };

    const newlyUnlocked = findNewlyUnlocked(fullContext, achievements);
    if (newlyUnlocked.length > 0) {
      setAchievements(prev => [...prev, ...newlyUnlocked.map((a) => a.id)]);
      const bonusXP = newlyUnlocked.reduce((sum, a) => sum + (a.xpReward || 0), 0);
      if (bonusXP > 0) addXP(bonusXP, 'Achievements unlocked');
      triggerCelebration();
    }

    return newlyUnlocked.map((a) => a.id);
  }, [achievements, daysVisited, xp, addXP]);

  /**
   * Get the current level data
   */
  const getLevel = useCallback(() => {
    return level;
  }, [level]);

  /**
   * Reset all gamification progress
   */
  const resetProgress = useCallback(() => {
    setXP(0);
    setLevel(LEVELS[0]);
    setAchievements([]);
    setStreak(0);
    setDaysVisited(0);
    setLastActiveDate(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo(() => ({
    // State
    xp,
    level,
    achievements,
    streak,
    daysVisited,
    lastActiveDate,

    // Constants
    LEVELS,
    ACHIEVEMENTS,
    XP_REWARDS,

    // Computed
    xpToNextLevel: (() => {
      const nextLevel = LEVELS.find(l => l.xpRequired > xp);
      return nextLevel ? nextLevel.xpRequired - xp : 0;
    })(),
    levelProgress: (() => {
      const nextLevel = LEVELS.find(l => l.xpRequired > xp);
      if (!nextLevel) return 100;
      const prevRequired = level.xpRequired;
      const nextRequired = nextLevel.xpRequired;
      return Math.round(((xp - prevRequired) / (nextRequired - prevRequired)) * 100);
    })(),

    // Methods
    addXP,
    checkAchievements,
    getLevel,
    resetProgress,
  }), [xp, level, achievements, streak, daysVisited, lastActiveDate, addXP, checkAchievements, getLevel, resetProgress]);

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export default GamificationContext;
